// Lobby games hook — Dark Launch sprint.
//
// Single source of truth for the 15-card lobby data. Calls
// GET /api/me/games once per page load, caches the result at module
// scope, and exposes a `refresh()` so consumers can re-fetch after a
// successful dev-pin verification (so the unlocked game starts showing
// up in `myDevGrants` immediately).
//
// Why module-level cache instead of useEffect-per-component?
//   * GameGrid lives inside Home which mounts/unmounts when the user
//     navigates around. Without a cache we'd re-fetch on every nav.
//   * DevSandboxRoute also reads this data on game-launch; we want
//     both consumers to see the same in-flight Promise.
//
// The cache invalidates on `refresh()` and on logout (cleared via
// `__lobbyCache.reset()` from the auth hook on sign-out).
import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';

export type LobbyGame = {
  id: string;
  displayName: string;
  tag: string | null;
  isLocked: boolean;
  sortOrder: number;
  themeGradient: string | null;
  themeEmoji: string | null;
  logoPath: string | null;
  unlockHint: string;
};

type LobbyData = {
  games: LobbyGame[];
  myDevGrants: string[];
};

type LobbyState = {
  data: LobbyData | null;
  error: string | null;
  pending: Promise<LobbyData> | null;
};

const state: LobbyState = { data: null, error: null, pending: null };
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

async function fetchLobby(): Promise<LobbyData> {
  const r = await api.get<LobbyData>('/me/games');
  state.data = r;
  state.error = null;
  state.pending = null;
  emit();
  return r;
}

function ensure(): Promise<LobbyData> {
  if (state.data) return Promise.resolve(state.data);
  if (state.pending) return state.pending;
  state.pending = fetchLobby().catch((e) => {
    state.error = (e as ApiError).message || 'Failed to load games';
    state.pending = null;
    emit();
    throw e;
  });
  emit();
  return state.pending;
}

export const lobbyCache = {
  /** Force a re-fetch on the next tick. Used after PIN verify. */
  refresh(): Promise<LobbyData> {
    state.data = null;
    state.pending = null;
    return ensure();
  },
  /** Clear all state (called on logout). */
  reset(): void {
    state.data = null;
    state.error = null;
    state.pending = null;
    emit();
  },
  /** Synchronous peek — undefined if not yet fetched. */
  peek(): LobbyData | null {
    return state.data;
  },
};

/**
 * React hook: returns the cached lobby data, kicks the fetch if it
 * hasn't started, and re-renders the subscribed component whenever
 * the cache updates (refresh, error, etc.).
 */
export function useLobbyGames(): {
  data: LobbyData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<LobbyData>;
} {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    if (!state.data && !state.pending) ensure().catch(() => { /* swallow — surfaced via state.error */ });
    return () => { listeners.delete(fn); };
  }, []);

  const refresh = useCallback(() => lobbyCache.refresh(), []);

  return {
    data: state.data,
    loading: !state.data && !state.error,
    error: state.error,
    refresh,
  };
}
